<template>
  <section-component :is-hero="true" class="start-team-page">
    <div class="container">
      <div class="text-center">
        <span class="uppercase text-center text-dark-grey font-medium mb-4">
          {{ $t("title") }}
        </span>
        <h1 class="font-bold text-h2">
          {{ $t("headline") }}
        </h1>
      </div>

      <div class="grid grid-cols-1 m:grid-cols-2 gap-8">
        <form-component class="max-w-none col-span-1 bg-white rounded-lg p-6" @submit="onSubmit">
          <notification v-if="error" class="start-team-page__error start-team-page__item" type="error">
            {{ $t("error") }}
          </notification>
          <h4 class="mb-12 text-center" v-html="$t('seatSubheadline', { minSeats, maxSeats })" />

          <input-seat
            class="start-team-page__seats"
            :min="minSeats"
            :max="maxSeats"
            :step="stepSeats"
            :value="seats"
            @change="onSeatsChange"
          />
          <div class="flex justify-between gap-2">
            <h4 class="text-h4 text-midnight font-bold">Total:</h4>
            <span v-if="isPlanLoading" class="text-h4 text-midnight font-bold">-</span>
            <span v-else data-testid="price-markup" class="text-h4 text-midnight font-bold" v-html="priceMarkup" />
          </div>

          <button-component
            class="w-full bg-midnight text-white border-none hover:bg-midnight/80 mb-6"
            :copy="$t('cta')"
            :disabled="loading"
          />

          <p class="text-dark-grey mb-4">
            {{ $t("moreSeats") }}
          </p>

          <button-link
            :href="bookACallLink"
            class="w-full bg-pale-mint-grey text-midnight border-none hover:bg-pale-mint-grey/80 mb-6"
          >
            {{ $t("bookACall") }}
          </button-link>
        </form-component>

        <div class="col-span-1 bg-white rounded-lg p-6">
          <h2 class="font-bold text-h3 text-center">{{ $t("planName") }}</h2>
          <p class="mb-6 text-center text-mid-grey">
            {{ $t("maxSeats") }}
          </p>
          <p class="text-dark-grey">
            {{ $t("selfServePlan") }}
          </p>

          <hr class="border-blue my-6" />

          <ul class="list-none flex flex-col gap-4">
            <li v-for="feature in $t('features')" :key="feature" class="flex items-center gap-4">
              <check-icon class="size-6" />
              <span class="text-p1">{{ feature }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section-component>
</template>

<script>
import { mapState, mapGetters } from "vuex"
import buttonComponent from "@/shared/button"
import FormComponent from "@/shared/form/form"
import SectionComponent from "@/shared/section"
import InputSeat from "@/pages/business/components/input-seat"
import Notification from "@/shared/notification"
import { trackEvent } from "@/services/tracking"
import CheckIcon from "@/component-library/icons/check-icon"

const HUB_SPOT_B2B_BOOK_A_DEMO_FORM_URL = "https://meetings-eu1.hubspot.com/tim-sonmez/inbound-en-round-robin"

const HUB_SPOT_B2B_ACQUISITION_FORM_LOCAL_STORAGE_KEY_PREFIX = "hubspot_b2b_form_"

export default {
  name: "StartTeamPage",
  components: {
    buttonComponent,
    FormComponent,
    SectionComponent,
    InputSeat,
    Notification,
    CheckIcon
  },
  computed: {
    ...mapState("business", ["seats", "minSeats", "maxSeats", "stepSeats", "isActiveSubscriber", "loading", "error"]),
    ...mapGetters("business", ["teamOwnerPrice", "priceMarkup", "isPlanLoading"]),
    bookACallLink() {
      const baseUrl = HUB_SPOT_B2B_BOOK_A_DEMO_FORM_URL
      const params = new URLSearchParams()

      const formFields = ["email", "firstname", "lastname"]

      formFields.forEach((key) => {
        const value = localStorage.getItem(`${HUB_SPOT_B2B_ACQUISITION_FORM_LOCAL_STORAGE_KEY_PREFIX}${key}`)
        if (value !== null) {
          params.set(key, value)
        }
      })

      const url = new URL(baseUrl)
      url.search = params.toString()

      return url.toString()
    }
  },
  created() {
    this.$store.dispatch("business/seats", this.seats)
  },
  methods: {
    onSeatsChange(value) {
      return this.$store.dispatch("business/seats", value)
    },
    onSubmit(event) {
      event.preventDefault()
      trackEvent("body-20210217-team_form", "1-submit", this.seats)
      return this.$store.dispatch("business/submit")
    }
  },
  i18n: {
    messages: {
      en: {
        title: "Checkout",
        headline: "Team Plan - Annual subscription",
        cta: "Confirm seats",
        moreSeats: "Do you need more seats?",
        bookACall: "Book a call",
        features: [
          "Blinkist Premium + 8,000 Blinks",
          "Mobile App & Web access",
          "Spaces & Shorts",
          "Blinkist Live",
          "AI Pathway Builder",
          "Insights Dashboard",
          "Custom Collections"
        ],
        seatSubheadline: "How many seats do you need?",
        perYear: "&nbsp;/&nbsp;year",
        error: "An error occurred. Please try again.",
        demo: "If you’re more than {maxSeats},",
        contactLink: "get in touch",
        maxSeats: "Up to 50 seats",
        planName: "Team Plan",
        selfServePlan:
          "This self-serve plan is perfect for small organizations and teams who want to get started in minutes.",
        disclaimer: "You’ll be billed annually. The owner seat will always be free. Cancel anytime, VAT may apply. ",
        consent:
          "One last thing—please tick this box to confirm you have permission to see the email addresses of people who join your team, and change the status of their account."
      },
      de: {
        title: "Kasse",
        headline: "Team-Plan - Jahresabonnement",
        cta: "Plätze bestätigen",
        moreSeats: "Benötigen Sie mehr Plätze?",
        bookACall: "Rufen Sie uns an",
        features: [
          "Blinkist Premium + 8.000 Blinks",
          "Mobile App & Web-Zugang",
          "Spaces & Shorts",
          "Blinkist Live",
          "KI-Pfad-Builder",
          "Insights Dashboard",
          "Benutzerdefinierte Sammlungen"
        ],
        seatSubheadline: "Wie viele Plätze benötigen Sie?",
        perYear: "/ Jahr",
        error: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
        demo: "Wenn Sie mehr als {maxSeats} sind,",
        contactLink: "kontaktieren Sie uns",
        maxSeats: "Bis zu 50 Plätze",
        planName: "Team-Plan",
        selfServePlan:
          "Dieser Selbstbedienungsplan ist perfekt für kleine Organisationen und Teams, die in wenigen Minuten loslegen möchten.",
        disclaimer:
          "Die Abrechnung erfolgt jährlich. Der Besitzerplatz ist immer kostenlos. Jederzeit kündbar, MwSt. kann anfallen. ",
        consent:
          "Noch eine letzte Sache—bitte aktivieren Sie dieses Kontrollkästchen, um zu bestätigen, dass Sie die Berechtigung haben, die E-Mail-Adressen der Personen zu sehen, die Ihrem Team beitreten, und den Status ihres Kontos zu ändern."
      },
      es: {
        title: "Finalizar compra",
        headline: "Plan de Equipo - Suscripción anual",
        cta: "Confirmar puestos",
        moreSeats: "¿Necesitas más puestos?",
        bookACall: "Reservar una llamada",
        features: [
          "Blinkist Premium + 8,000 Blinks",
          "Acceso a la App móvil y Web",
          "Spaces y Shorts",
          "Blinkist Live",
          "Constructor de Rutas con IA",
          "Panel de Insights",
          "Colecciones Personalizadas"
        ],
        seatSubheadline: "¿Cuántos puestos necesitas?",
        perYear: "&nbsp;/&nbsp;año",
        error: "Ocurrió un error. Por favor, inténtalo de nuevo.",
        demo: "Si son más de {maxSeats},",
        contactLink: "ponte en contacto",
        maxSeats: "Hasta 50 usuarios",
        planName: "Plan de Equipo",
        selfServePlan:
          "Este plan de autoservicio es perfecto para pequeñas organizaciones y equipos que quieren comenzar en minutos.",
        disclaimer:
          "Se te facturará anualmente. El puesto del propietario siempre será gratuito. Cancela en cualquier momento, puede aplicar IVA.",
        consent:
          "Una última cosa: por favor marca esta casilla para confirmar que tienes permiso para ver las direcciones de correo electrónico de las personas que se unan a tu equipo y cambiar el estado de su cuenta."
      },
      pt: {
        title: "Finalizar compra",
        headline: "Plano de Equipe - Assinatura anual",
        cta: "Confirmar vagas",
        moreSeats: "Você precisa de mais vagas?",
        bookACall: "Agendar uma chamada",
        features: [
          "Blinkist Premium + 8,000 Blinks",
          "Acesso ao App móvel e Web",
          "Spaces e Shorts",
          "Blinkist Live",
          "Construtor de Caminhos com IA",
          "Painel de Insights",
          "Coleções Personalizadas"
        ],
        seatSubheadline: "Quantas vagas você precisa?",
        perYear: "&nbsp;/&nbsp;ano",
        error: "Ocorreu um erro. Por favor, tente novamente.",
        demo: "Se vocês são mais de {maxSeats},",
        contactLink: "entre em contato",
        maxSeats: "Até 50 usuários",
        planName: "Plano de Equipe",
        selfServePlan:
          "Este plano de autoatendimento é perfeito para pequenas organizações e equipes que querem começar em minutos.",
        disclaimer:
          "Você será cobrado anualmente. A vaga do proprietário sempre será gratuita. Cancele a qualquer momento, impostos podem ser aplicados.",
        consent:
          "Uma última coisa—por favor marque esta caixa para confirmar que você tem permissão para ver os endereços de e-mail das pessoas que se juntarem à sua equipe e alterar o status de sua conta."
      }
    }
  }
}
</script>

<style lang="scss">
.start-team-page {
  position: relative;

  background-color: $pale-mint-grey;

  counter-reset: subheadline;

  &__notification {
    margin-bottom: $spacing-32;

    @include media(min-width, l) {
      @include grid-css-element(8, 3);
    }
  }

  #{&} &__form {
    @include container-padding;

    max-width: none;
    padding-top: $spacing-32;
    padding-bottom: $spacing-32;

    background-color: $white;
    border-radius: $border-radius-4;

    @include media(min-width, m) {
      @include grid-css;

      padding-top: $spacing-48;
      padding-bottom: $spacing-48;
    }

    @include media(min-width, l) {
      @include grid-css-element(10, 2);
    }
  }

  &__error {
    margin-bottom: $spacing-32;
  }

  &__item {
    @include media(min-width, m) {
      @include grid-css-element(2);

      &--half {
        @include grid-css-element(6);
      }
    }
  }

  &__subheadline {
    counter-increment: section;

    &:before {
      content: counter(section) ". ";
    }

    @include media(min-width, m) {
      @include grid-css-element(2);
    }
  }

  &__seat-note {
    margin-top: -$spacing-8;
    margin-bottom: $spacing-24;

    @include media(min-width, m) {
      @include grid-css-element(2);
    }
  }

  &__input {
    margin-bottom: $spacing-32;
  }

  &__seats {
    margin-bottom: $spacing-16;
  }

  &__demo {
    margin-bottom: $spacing-32;
  }

  &__price {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    margin-bottom: $spacing-8;
    padding: $spacing-24;

    color: $mid-grey;
    font-size: font-size(p0);

    background-color: $background-blue;

    div:first-child {
      margin-bottom: $spacing-16;
    }
  }

  &__price-value {
    color: $midnight;
  }

  &__price-loading &:before {
    content: "-";
  }

  &__disclaimer {
    @include font-style(legal);

    margin-bottom: $spacing-32;
  }

  &__consent {
    @include font-style(legal);

    margin-bottom: $spacing-32;
  }

  &__footer {
    display: flex;
    justify-content: center;

    @include media(min-width, m) {
      @include grid-css-element(2);
    }
  }

  #{&} &__cta {
    flex-basis: $wide-block-width;
  }

  &__wave {
    position: absolute;
    bottom: 0;
    z-index: 1;

    width: 100%;
  }
}
</style>
